import { styled, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { Typography } from '../Typography/Typography';
import { WorkflowStage } from '../../types';
import { STAGE_LABEL, SUBMITTER_TOKEN, CLOSED_TOKEN } from './WorkflowStagesStrip';

/** Resolve a node's display label: per-workflow override (if a non-empty string) else the default. */
function resolveLabel(token: string, defaultLabel: string, overrides?: Record<string, string> | null): string {
  const override = overrides?.[token];
  return typeof override === 'string' && override.trim() ? override : defaultLabel;
}

type TimelineNode = {
  key: string;
  label: string;
  kind: 'endpoint' | 'stage';
  /** 1-based step number; endpoints before any stage get a number too, the final Closed node gets a check. */
  step?: number;
  isClosed?: boolean;
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.02 } },
};
const nodeVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 460, damping: 30 } },
};

type Props = {
  /** Ordered approval stages the timesheet walks through. Empty = no approval (Submitter -> Closed). */
  stages: WorkflowStage[];
  /** When changed, remounts the strip so the stagger animation replays (user form passes workflow id). */
  animationKey?: string | number;
  /** Optional per-workflow display-label overrides keyed by node token (SUBMITTER, <STAGE>, CLOSED). */
  labels?: Record<string, string> | null;
};

/**
 * Approval-pipeline timeline (design refresh): numbered step circles joined by connectors, with the
 * node label beneath each. `Submitter -> <stages, in order> -> Closed`. The submitter + stage nodes are
 * numbered sequentially; the terminal Closed node shows a check. Purely presentational — it reads the
 * same `stages`/`labels` the old strip did. The original `WorkflowStagesStrip` is kept for the
 * standalone Workflows page.
 */
export function WorkflowTimeline({ stages, animationKey, labels }: Props) {
  const { colors } = useTheme();

  const nodes = useMemo<TimelineNode[]>(() => {
    const list: TimelineNode[] = [
      {
        key: 'submitter',
        label: resolveLabel(SUBMITTER_TOKEN, 'Submitter', labels),
        kind: 'endpoint',
      },
      ...stages.map((stage) => ({
        key: stage,
        label: resolveLabel(stage, STAGE_LABEL[stage] ?? stage, labels),
        kind: 'stage' as const,
      })),
      {
        key: 'closed',
        label: resolveLabel(CLOSED_TOKEN, 'Closed', labels),
        kind: 'endpoint' as const,
        isClosed: true,
      },
    ];
    // Number every node except the terminal Closed node, left to right (1-based).
    let step = 0;
    return list.map((node) => (node.isClosed ? node : { ...node, step: ++step }));
  }, [stages, labels]);

  return (
    <Row
      key={animationKey}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      aria-label={`Approval pipeline: ${nodes.map((n) => n.label).join(' then ')}`}
    >
      {nodes.map((node, index) => {
        const isStage = node.kind === 'stage';
        return (
          <Segment key={node.key} variants={nodeVariants}>
            {index > 0 && <Connector style={{ backgroundColor: colors.border.default }} />}
            <NodeCol>
              <Circle
                style={{
                  backgroundColor: isStage ? colors.surfaceAccent.default : 'transparent',
                  borderColor: isStage ? colors.surfaceAccent.default : colors.textAccent.disabled,
                }}
              >
                {node.isClosed ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12.5l4.5 4.5L19 7"
                      stroke={colors.text.secondary}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <Typography
                    variant="caption"
                    color={isStage ? colors.textAccent.inverse : colors.text.secondary}
                  >
                    {node.step}
                  </Typography>
                )}
              </Circle>
              <Label>
                <Typography
                  variant="caption"
                  color={isStage ? colors.text.main : colors.text.secondary}
                >
                  {node.label}
                </Typography>
              </Label>
            </NodeCol>
          </Segment>
        );
      })}
    </Row>
  );
}

const Row = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  width: 100%;
`;

// A segment is [optional connector][node]. The first segment has no leading connector, so nodes stay
// evenly distributed and connectors fill the gaps between circle centers.
const Segment = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  &:first-of-type {
    flex: 0 0 auto;
  }
`;

const CIRCLE = 28;

const Connector = styled('div')`
  flex: 1;
  height: 2px;
  min-width: 12px;
  margin-top: ${CIRCLE / 2 - 1}px;
`;

const NodeCol = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  width: 84px;
`;

const Circle = styled('div')`
  width: ${CIRCLE}px;
  height: ${CIRCLE}px;
  border-radius: 50%;
  border: 1.5px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Label = styled('div')`
  text-align: center;
  line-height: 1.2;
`;
